// From your app (ensure this matches exactly)
export interface ColumnConfig {
    label: string;
    jsonKey: string;
    dataType:
    | 'number'
    | 'string'
    | 'date'
    | 'boolean'
    | 'textbool'
    | 'toggle'
    | 'password'
    | 'text-wrap'
    | 'custom-link'
    | 'dateOnly';
    width?: 'full' | 'ellipsis';
    toggleUrl?: string;
    hideOnToggle?: boolean;
    isMarkDownSupported?: boolean;
    toUppercase?: boolean;
    isSortable?: boolean;
}

/** Row/column coloring rule you can pass from any report */
export type ColorRule = {
    columns?: string[];
    rowWhen?: (row: any) => boolean;
    cellWhen?: (row: any, field: string) => boolean;
    textColor?: string;
    fillColor?: string;
    bold?: boolean;
};

/** Export options to control spacing and "fit all columns" behavior */
export type ExportPdfOptions = {
    title?: string;
    watermarkText?: string;
    pageSize?: 'A4' | 'A3' | 'LETTER' | 'LEGAL';   // default A4
    pageOrientation?: 'portrait' | 'landscape';    // default landscape
    baseFontSize?: number;
    minFontSize?: number;
    targetPadding?: number;
    lineHeight?: number;
    /** If true, try to keep ALL columns in one table by shrinking font/padding and upgrading pageSize to A3 if needed */
    forceSingleTable?: boolean;                    // default true    
    colorRules?: ColorRule[];
    fieldsToTrim?: Set<string>;
    data?: any[];
    exportFileName?: string;    
};

//  How each column should appear in the PDF. 
export type PdfColumnRule = {
    field: string;
    width?: 'auto' | '*' | number;
    wrap?: 'wrap' | 'ellipsis' | 'clip';
    maxChars?: number;
    maxLines?: number;
    monospace?: boolean;
    lineHeight?: number;
    showEdges?: { head: number; tail: number; mask?: string };
    toUppercase?: boolean;
    preprocess?: (raw: unknown) => string;
};

// pdfmake column descriptor used in the export
export type PdfColumn = { header: string; field: string; width?: 'auto' | '*' | number };


/** Strip all newlines and collapse whitespace to single spaces */
export function stripNewlines(text: string): string {
    return text.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Truncate hard to 40 chars (no ellipsis) */
export function truncate40(text: string): string {
    return text.length > 40 ? text.slice(0, 40) : text;
}


// Map pdf sizes in points (A4: 595x842, A3: 842x1191)
export function getPageSizePoints(size: 'A4' | 'A3' | 'LETTER' | 'LEGAL') {
    switch (size) {
        case 'A3': return { w: 1191, h: 842 };
        case 'LETTER': return { w: 612, h: 792 };
        case 'LEGAL': return { w: 612, h: 1008 };
        case 'A4':
        default: return { w: 595, h: 842 };
    }
}

export function innerWidth(pageSize: 'A4' | 'A3' | 'LETTER' | 'LEGAL', orientation: 'portrait' | 'landscape', left = 40, right = 40) {
    const { w, h } = getPageSizePoints(pageSize);
    const width = orientation === 'landscape' ? h : w;
    return width - left - right;
}

/** Heuristic per-column width based on header+sample cell lengths */
export function estimateColumnWidth(
    headerText: string,
    rows: any[],
    field: string,
    fontSize = 10,
    sampleCount = 50,
    charFactor = 0.6,
    padding = 0
): number {
    const headerLen = (headerText ?? '').length;
    let maxLen = headerLen;
    const limit = Math.min(rows.length, sampleCount);
    for (let i = 0; i < limit; i++) {
        const v = rows[i]?.[field];
        const s = v == null ? '' : String(v);
        maxLen = Math.max(maxLen, s.length);
    }
    return Math.ceil(maxLen * charFactor * fontSize + padding);
}

/** Compute fitting settings to keep ALL columns in a single table */
export function computeFitSettings(
    headers: string[],
    fields: string[],
    rows: any[],
    pageSize: 'A4' | 'A3' | 'LETTER' | 'LEGAL',
    orientation: 'portrait' | 'landscape',
    margins: [number, number, number, number],
    baseFontSize = 10,
    minFontSize = 8,
    targetPadding = 0
) {
    const [left, , right] = margins;
    let currentSize = baseFontSize;
    let currentPage = pageSize;

    const tryInner = () => innerWidth(currentPage, orientation, left, right);
    const required = (font: number) =>
        fields
            .map((f, i) => estimateColumnWidth(headers[i], rows, f, font, 50, 0.6, targetPadding))
            .reduce((a, b) => a + b, 0);

    // First attempt: shrink font size until fits or reach minFontSize
    while (required(currentSize) > tryInner() && currentSize > minFontSize) {
        currentSize -= 0.5; // small decrements for precision
    }

    // If still not fitting, upgrade page to A3 landscape (wider)
    if (required(currentSize) > tryInner() && currentPage !== 'A3') {
        currentPage = 'A3';
    }

    return { fontSize: currentSize, pageSize: currentPage };
}

// A4 landscape ≈ 842pt - margins.
export function getPageInnerWidth(pageOrientation: 'portrait' | 'landscape', left = 40, right = 40): number {
    const pageWidth = pageOrientation === 'landscape' ? 842 : 595; // A4 sizes in points
    return pageWidth - left - right;
}

// Split columns into chunks that fit within pageInnerWidth.
export function chunkColumnsToFit(
    pdfColumns: { header: string; field: string }[],
    rows: any[],
    pageInnerWidth: number,
    fontSize = 10
) {
    const chunks: { headers: any[]; fields: string[]; widths: any[] }[] = [];
    let currentChunkHeaders: any[] = [];
    let currentChunkFields: string[] = [];
    let currentChunkWidths: any[] = [];
    let runningWidth = 0;

    for (const col of pdfColumns) {
        const w = estimateColumnWidth(col.header.toUpperCase(), rows, col.field, fontSize);
        // If adding this column would exceed the page, start a new chunk
        if (runningWidth + w > pageInnerWidth && currentChunkHeaders.length > 0) {
            chunks.push({
                headers: currentChunkHeaders,
                fields: currentChunkFields,
                widths: currentChunkWidths
            });
            currentChunkHeaders = [];
            currentChunkFields = [];
            currentChunkWidths = [];
            runningWidth = 0;
        }

        currentChunkHeaders.push({
            text: col.header.toUpperCase(),
            bold: true,
            color: '#0D47A1',
            fillColor: '#E3F2FD',
            margin: [0, 3, 0, 3]
        });
        currentChunkFields.push(col.field);
        currentChunkWidths.push('auto'); // use 'auto'; stretching handled by layout
        runningWidth += w;
    }

    // Push the last chunk
    if (currentChunkHeaders.length) {
        chunks.push({
            headers: currentChunkHeaders,
            fields: currentChunkFields,
            widths: currentChunkWidths
        });
    }

    return chunks;
}

/** Map ColumnConfig[] -> PdfColumn[] */
export function mapColumnConfigToPdfColumns(configs: ColumnConfig[]): PdfColumn[] {
    return configs.map<PdfColumn>((c) => {
        let width: 'auto' | '*' | number = 'auto';
        switch (c.width) {
            case 'full':
                width = '*';
                break;
            case 'ellipsis':
            default:
                width = 'auto';
        }
        return { header: c.label, field: c.jsonKey, width };
    });
}

/** Type-aware cell formatter for the PDF */
export function formatCellByType(cfg: ColumnConfig, value: unknown, locale = 'en-IN'): string {
    try {
        if (value == null || value === undefined) return '';

        switch (cfg.dataType) {
            case 'number': {
                const n = Number(value);
                return Number.isFinite(n) ? n.toLocaleString(locale) : String(value || '');
            }
            // case 'date':
            // case 'dateOnly': {
            //     const d = new Date(value as any);
            //     if (!isNaN(d.getTime())) {
            //         // Adjust format as needed
            //         return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
            //     }
            //     return String(value);
            // }
            case 'boolean': {
                const b = /^(true|1)$/i.test(String(value || ''));
                return b ? 'Yes' : 'No';
            }
            case 'toggle': {
                const b = /^(true|1|on|yes)$/i.test(String(value || ''));
                return b ? 'Yes' : 'No';
            }
            case 'password': {
                return '••••••';
            }
            case 'text-wrap':
            case 'textbool':
            case 'string':
            default: {
                let text = String(value || '');
                if (cfg.toUppercase) text = text.toUpperCase();
                return text;
            }
        }
    } catch (error) {
        console.warn('Error in formatCellByType:', error);
        return String(value || '');
    }
}

export function formatCell(value: unknown): string {
    if (value === null || value === undefined) return '';
    // Basic formatting; expand with your own logic (dates, currency, etc.)
    return String(value);
}

export function slugify(s: string): string {
    return s.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
}

export function timestamp() {
    const d = new Date();
    return `${d.getFullYear()}${(d.getMonth() + 1 + '').padStart(2, '0')}${(d.getDate() + '').padStart(2, '0')}-${(d.getHours() + '').padStart(2, '0')}${(d.getMinutes() + '').padStart(2, '0')}`;
}

/** Build a quick lookup map for rules */
export function buildRuleMap(rules: PdfColumnRule[]): Map<string, PdfColumnRule> {
    return new Map(rules.map(r => [r.field, r]));
}

/** Mask middle section keeping first head and last tail chars */
export function maskEdges(text: string, head = 6, tail = 4, mask = '…'): string {
    if (text.length <= head + tail) return text;
    return `${text.slice(0, head)}${mask}${text.slice(-tail)}`;
}

/** Truncate with ellipsis to a max number of characters */
export function ellipsis(text: string, maxChars: number, suffix = '…'): string {
    return text.length > maxChars ? text.slice(0, Math.max(0, maxChars - suffix.length)) + suffix : text;
}

/** Estimate truncation to maxLines by measuring characters per line (simple heuristic) */
export function truncateToLines(text: string, maxLines: number, approxCharsPerLine = 80, suffix = '…'): string {
    const maxChars = maxLines * approxCharsPerLine;
    return ellipsis(text, maxChars, suffix);
}

/** Apply rule-driven formatting to a value (string returned) */
export function formatByRule(value: unknown, rule?: PdfColumnRule, locale = 'en-IN'): string {
    if (value == null) return '';
    let text = String(value);

    if (rule?.preprocess) {
        text = rule.preprocess(text);
    }

    // optional uppercase
    if (rule?.toUppercase) {
        text = text.toUpperCase();
    }

    // edge masking first
    if (rule?.showEdges) {
        const { head, tail, mask = '…' } = rule.showEdges;
        text = maskEdges(text, head, tail, mask);
    }

    const maxChars = rule?.maxChars ?? 120;

    switch (rule?.wrap) {
        case 'wrap': {
            // Soft-wrap with maxLines cap
            const maxLines = rule?.maxLines ?? 3;
            text = truncateToLines(text, maxLines, 80, '…'); // 80 chars/line heuristic; tweak per font/size
            break;
        }
        case 'clip': {
            text = text.slice(0, maxChars);
            break;
        }
        case 'ellipsis':
        default: {
            text = ellipsis(text, maxChars);
            break;
        }
    }

    return text;
}
